/**
 * R12 step 1: which body an entity is, and what follows from it (ARCHITECTURE §5.13). An entity that names its body
 * is that; one that doesn't is what the clock made it today — a unit is a person on the action clock and the classic
 * vehicle on the turn clock, a structure is a structure (the emplacement a fixed vehicle), a drone a drone. The
 * observer and the felt gravity read the body, not the clock. Pure.
 */
import type { BodyKind, BodyDef, BodyObserver, SeatDef, StructureJob, RuinJob, RuinState, GameState, PlayerState, StructureState, StructureType, DroneState, ScaleProfileName, SeatState, MountArmament } from '../types';
import { BODIES, RUIN_JOBS, HATCH_SURVIVAL_THRESHOLD, type BodyKey } from '../constants/body';
import { LOS_EYE_HEIGHT, MORTAR, TANK_GUN } from '../constants';
import { personSet, scaleProfile } from './scaleProfile';

type Clocked = Pick<GameState, 'config'>;

/**
 * A unit's body kind: its own. `body` is set when the unit is made (`completeMatchState`, from the clock's reading
 * at creation — `unitBodyAtCreation`); a record from before R12 step 1 reads as the classic unit. No clock read
 * on the tick path (64.6).
 */
export function unitBodyKind(unit: Pick<PlayerState, 'body'>): BodyKind {
  return unit.body ?? 'vehicle';
}
/** The body a new unit gets from the clock it is made for: a person on the action clock, the classic vehicle on the turn clock. */
export function unitBodyAtCreation(state: Clocked): BodyKind {
  return state.config.clock === 'action' ? 'person' : 'vehicle';
}
/** A structure's body kind: its own, or its type's (the emplacement is a fixed vehicle with a seat). */
export function structureBodyKind(s: Pick<StructureState, 'body' | 'type'>): BodyKind {
  return s.body ?? BODIES[s.type].kind;
}
export function droneBodyKind(d: Pick<DroneState, 'body'>): BodyKind { return d.body ?? 'drone'; }

/** The body table's row for a unit: the person, or the classic unit (a vehicle). */
export function unitBodyDef(unit: Pick<PlayerState, 'body'>): BodyDef {
  return BODIES[unitBodyKind(unit) === 'person' ? 'person' : 'unit'];
}
export function structureBodyDef(s: Pick<StructureState, 'type'>): BodyDef { return BODIES[s.type as BodyKey]; }

/** The observer a unit perceives as (core/perception.ts): from its body. */
export function observerOf(unit: Pick<PlayerState, 'body'>): BodyObserver {
  return unitBodyDef(unit).observer;
}
/** Line-of-sight eye height of a body kind: the vehicle's mast is the constant; the person's eye scales (= eyeHeightFor by observer). */
export function eyeHeightOfBody(observer: BodyObserver, scale: ScaleProfileName | undefined): number {
  return observer === 'vehicle' ? LOS_EYE_HEIGHT : personSet(scale).losEyeHeight;
}
/** Gravity felt by a unit, as a fraction of the planet's g (ruling 15a): its body's family. */
export function gravityScaleOf(unit: Pick<PlayerState, 'body'>, state: Pick<GameState, 'config'>): number {
  return unitBodyDef(unit).gravity === 'person' ? scaleProfile(state.config.scale).personGravity : 1;
}

/** X18: a structure's job (the table's; a building has an empty one). */
export function structureJob(s: Pick<StructureState, 'type'>): StructureJob {
  return BODIES[s.type].job ?? {};
}
/** X18: a standing structure with this job (a sensor, supply, spawn...). */
export function standingWithJob<K extends keyof StructureJob>(s: Pick<StructureState, 'type' | 'health'>, job: K): boolean {
  return s.health > 0 && structureJob(s)[job] !== undefined;
}
/** X18: the structure types the sector's word names (`depot` → the landing pad, `sensor` → the antenna). */
export function structureTypeForSector(kind: NonNullable<StructureJob['sector']>[number]): StructureType {
  const hit = (Object.keys(BODIES) as BodyKey[]).find(k => BODIES[k].job?.sector?.includes(kind));
  if (!hit) throw new Error(`no structure for the sector's ${kind}`);
  return hit as StructureType;
}
/** X18: a ruin's job (core/claims.ts, core/uplink.ts). */
export function ruinJob(r: Pick<RuinState, 'ruinType'>): RuinJob { return RUIN_JOBS[r.ruinType]; }

/**
 * 69.2b: the team a structure serves — its owner unit's (a planted beacon or relay: its planter's); null for nobody's.
 * The one way to read it: the waves' beacon, the relay's sight, the web (69.2c) and the arrivals (69.3a) all ask here.
 */
export function structureTeam(state: Pick<GameState, 'players'>, s: Pick<StructureState, 'ownerId'>): number | null {
  return state.players.find(p => p.id === s.ownerId)?.teamIndex ?? null;
}

/** A new structure's starting health: its body's (the table replaces the old two-constant function). */
export function structureHealth(type: StructureType): number { return BODIES[type].health; }
/** 69.61 step 6: a structure's full health as it started — a board base's HQ at GameConfig.hqHealth when set (DECISIONS 284 (a)), else its body's. */
export function structureFullHealth(state: Pick<GameState, 'board' | 'config'>, s: Pick<StructureState, 'id' | 'type'>): number {
  return state.config.hqHealth !== undefined && state.board?.bases.some(b => b.hq === s.id) ? state.config.hqHealth : structureHealth(s.type);
}
/**
 * The seats a new structure of this type starts with — the body's, empty — or undefined for the seatless kinds. 69.21: with an
 * `armament`, the body's bullet-gun seat carries it instead — another gun, or a launcher (the technical's variants).
 */
export function initialSeats(type: StructureType, armament?: MountArmament, tankStowage?: { he: number; solid: number }): StructureState['seats'] {
  const defs = BODIES[type].seats;
  if (!defs.length) return undefined;
  // 69.23: armed 'mortar', the first hatch passenger seat (the M113's cargo hatch) is the tube's crew seat; its .50 stays
  const mortarSeat = armament === 'mortar' ? defs.findIndex(d => d.role === 'passenger' && d.entry === 'hatch') : -1;
  return defs.map((d, i): SeatState => {
    const seat: SeatState = { role: d.role, occupant: null, weapons: [...(d.weapons ?? [])] };
    if (i === mortarSeat) return { ...seat, mortar: true };
    // 69.29: the tank's turret seat — its main gun (an explosive round in it, the rest of the stowage's default stowed) and its coax
    if (d.mainGun) return { ...seat, ...(d.guns?.length ? { guns: [...d.guns] } : {}), main: tankMain(tankStowage ?? TANK_GUN.stowage) };
    if (!d.guns?.length) return seat;
    if (armament === undefined || armament === 'mortar') return { ...seat, guns: [...d.guns] };   // 63.1d step 3: a seat's bullet guns
    return armament === 'rocket' || armament === 'missile' ? { ...seat, launcher: armament } : { ...seat, guns: [armament] };
  });
}

/** 69.29: a tank's main gun as it starts — an explosive round loaded (from the stowage when it has one, else a solid), the rest stowed. */
export function tankMain(stowage: { he: number; solid: number }): NonNullable<SeatState['main']> {
  const loaded = stowage.he > 0 ? 'he' : stowage.solid > 0 ? 'solid' : null;
  return { loaded, next: 'he', he: Math.max(0, stowage.he - (loaded === 'he' ? 1 : 0)), solid: Math.max(0, stowage.solid - (loaded === 'solid' ? 1 : 0)) };
}

/** 69.21: a technical (or any body with a bullet-gun seat) armed with `armament` — the field and its seats re-laid, empty. */
export function armStructure(s: StructureState, armament: MountArmament, mortarStore = MORTAR.store): StructureState {
  return { ...s, armament, seats: initialSeats(s.type, armament), ...(armament === 'mortar' ? { mortar: { rounds: mortarStore } } : {}) };   // (69.23: the tube's store)
}
/**
 * X31 / R12 step 5: does this structure drive — its body's row says `mobility: 'drive'` and it carries the drive's
 * state (heading, speed, build)? It moves under its driver every tick (core/drive.ts), so it is no static obstacle.
 * The motorcycle today; a transport later, by its row, with no new branch on its type.
 */
export function drives(s: Pick<StructureState, 'type' | 'drive'>): boolean {
  return !!s.drive && BODIES[s.type as BodyKey]?.mobility === 'drive';
}
/** 69.4 step 5 (DECISIONS 166): a vehicle for supply runs only (the supply truck) — the rides, loops and transport markers skip it; a convoy's leg alone drives it. */
export function cargoOnly(s: Pick<StructureState, 'type'>): boolean {
  return BODIES[s.type as BodyKey]?.cargoOnly === true;
}
/** Does a seat carry a weapon (a gun's post) — or is it a driver's or a passenger's? */
export function seatArmed(seat: Pick<SeatState, 'weapons'> & Partial<Pick<SeatState, 'guns' | 'launcher'>> | undefined): boolean {
  return (seat?.weapons?.length ?? 0) > 0 || (seat?.guns?.length ?? 0) > 0 || !!seat?.launcher;   // 63.1d step 3: or a bullet gun; 69.21: or a launcher
}
/** Does this body carry a seat? */
export function bodyHasSeat(type: StructureType): boolean { return BODIES[type].seats.length > 0; }

/**
 * Decision 124: what becomes of a seat's occupant when the hull dies. `blastShare` is the share of the killing
 * blow that reached the seat (0 = small arms or a blow the hull took whole; 1 = the whole blast at the hatch).
 * Below the hatch line the occupant dies; at a hatch it survives a blow under HATCH_SURVIVAL_THRESHOLD with
 * its health scaled by what got through, and bails out at the entry. Pure; step 4's one damage entry point
 * calls it on a vehicle's death. The emplacement keeps today's rule (its occupant unseated unhurt) until that step.
 */
export function hatchSurvival(seat: Pick<SeatDef, 'entry'>, blastShare: number, health: number): { survives: false } | { survives: true; health: number } {
  if (seat.entry !== 'hatch' || blastShare >= HATCH_SURVIVAL_THRESHOLD) return { survives: false };
  const left = health * (1 - blastShare / HATCH_SURVIVAL_THRESHOLD);
  return left > 0 ? { survives: true, health: left } : { survives: false };
}
